import React, { useContext, useMemo, useState } from 'react';
import { EDCaptionedValue } from '../../../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import EDColumns, {
  EDColumn,
} from '../../../../../../../BaseComponents/Columns/EDColumns';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { selectedId } from '../../../../../../../Components/MasterDetail/MasterDetailUtils';
import { TEDEKBokforderTYPEnum } from '../../../../../../../globalQueryTypes';
import {
  IBusinessCollectionProps,
  IObjektVy,
  IObjektVyComp,
} from '../../../../../../../Types/ObjektVy';
import Attribut from '../../../../../_Common/Components/BusinessCollectionComponents/Attribut/Attribut';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { ObjektLank } from '../../../../../_Common/Components/BusinessCollectionComponents/ObjektLank/ObjektLank';
import KonteringContext from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import { UtanordningQuery_node_TEDEKBokforder__Associations } from '../../Queries/_QueryTypes/UtanordningQuery';
import {
  filterUtbetalningssatt,
  validMessageUtbetalningsatt,
} from '../../Utils/UtanordningUtils';
import AttestantPicker from '../AttestantPicker/AttestantPicker';
import BokforderContext from '../BokforderContext/BokforderContext';
import { IUserBokforderKodbehorigheter } from '../BokforderContext/BokforderContextManager';

interface IProps extends IBusinessCollectionProps {}

const UtanordningBankkontoFormComp: IObjektVyComp = {
  caption: '',
  comp: (props: IProps) => <UtanordningBankontoFormView {...props} />,
};

const UtanordningBankontoFormView: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const bokforderContext = useContext(BokforderContext);
  const konteringContext = useContext(KonteringContext);
  const formContext = useContext(BusinessDataFormContext);

  const getKontodelDefsByKodplanId =
    konteringContext.konteringUtils.getKontodelDefsByKodplanId;

  const [valdAttestant, setValdAttestant] =
    useState<null | IUserBokforderKodbehorigheter>(null);

  const kontodelDefFromKodplan = useMemo(() => {
    return getKontodelDefsByKodplanId(
      bokforderContext.bokforderKodplan?.id ?? ''
    );
  }, [bokforderContext.bokforderKodplan?.id, getKontodelDefsByKodplanId]);

  const numberOfColumns = bokforderContext.isEditMode ? 4 : 3;
  const columnSpacing = bokforderContext.isEditMode ? 1 : 0;
  const bestalldBokfDatumDifferFromBokfDatum =
    formContext?.formState?.currentValues?.BetBokfdatum !==
    formContext?.formState?.currentValues?.BOKF_DATUM;

  return (
    <EDBox
      sx={{
        width: theme => `calc(100% - ${theme.spacing(2)})`,
        height: 'auto',
        paddingX: theme => theme.spacing(1),
      }}
    >
      <EDColumns spacing={columnSpacing}>
        <EDColumn md={numberOfColumns} pt={0}>
          <EDBox sx={{ flex: 1 }}>
            {bestalldBokfDatumDifferFromBokfDatum &&
            !bokforderContext.isEditMode ? (
              <EDCaptionedValue
                label="Beställningsdatum"
                name="bestalltBokfdatum"
                size="large"
              >
                <EDTypography display="inline-block">{` ${formContext?.formState?.currentValues?.BetBokfdatum} (bokförd: ${formContext?.formState?.currentValues?.BOKF_DATUM})`}</EDTypography>
              </EDCaptionedValue>
            ) : (
              <Attribut
                disableEditMode={!bokforderContext.isEditMode}
                name="BOKF_DATUM"
                customDisplayLabel="Beställningsdatum"
                disabled
                size="large"
              />
            )}
          </EDBox>
        </EDColumn>
        <EDColumn md={numberOfColumns} pt={0}>
          <EDBox sx={{ flex: 1 }}>
            <Attribut
              disableEditMode={!bokforderContext.isEditMode}
              name="U_BELOPP"
              size="large"
            />
          </EDBox>
        </EDColumn>
        <EDColumn md={numberOfColumns} pt={0}>
          <EDBox sx={{ flex: 1 }}>
            <ObjektLank<UtanordningQuery_node_TEDEKBokforder__Associations>
              associationName="Utbetalningssatt"
              editMode={bokforderContext.isEditMode}
              disableAssociationLink
              id={selectedId(props.id) ?? 'Utbetalningssatt'}
              size="ownblock"
              fullWidth
              validValues={{
                dataDependencies: [
                  'BETSATT',
                  'BORTTAGEN',
                  'Utbetalningsvag.AKTIV',
                  'Utbetalningsvag.BORTTAGEN',
                ],
                validValuesFunction: defaultValues => {
                  return filterUtbetalningssatt(
                    bokforderContext.bokforderVariant,
                    defaultValues
                  );
                },
              }}
            />
          </EDBox>
        </EDColumn>
      </EDColumns>

      <EDColumns spacing={columnSpacing}>
        <EDColumn md={numberOfColumns} pt={0}>
          <EDBox sx={{ flex: 1 }}>
            <Attribut
              disableEditMode={!bokforderContext.isEditMode}
              name="U_BETALNINGSMOTTAGARE"
              size="large"
            />
          </EDBox>
        </EDColumn>
        <EDColumn md={numberOfColumns} pt={0}>
          <EDBox sx={{ flex: 1 }}>
            <Attribut
              disableEditMode={!bokforderContext.isEditMode}
              name="U_BANK"
              size="large"
              isRequired
            />
          </EDBox>
        </EDColumn>
        <EDColumn md={numberOfColumns} pt={0}>
          <EDBox sx={{ flex: 1 }}>
            <Attribut
              disableEditMode={!bokforderContext.isEditMode}
              name="U_CLEARING_BANKKONTONUMMER"
              size="large"
            />
          </EDBox>
        </EDColumn>
      </EDColumns>
      <EDColumns spacing={columnSpacing}>
        {validMessageUtbetalningsatt.includes(
          formContext?.formState.currentValues?._Associations?.Utbetalningssatt
            ?.BETSATT
        ) ? (
          <EDColumn md={8} pt={0}>
            <EDBox sx={{ flex: 2 }}>
              <Attribut
                customDisplayLabel="Meddelande till mottagare (max 12 tecken)"
                disableEditMode={!bokforderContext.isEditMode}
                name="U_MEDDELANDE"
                size="large"
                maxInputLength={12}
              />
            </EDBox>
          </EDColumn>
        ) : (
          <EDColumn md={8} pt={0}>
            <EDBox sx={{ flex: 2 }}>
              <Attribut
                customDisplayLabel="Rubrik"
                disableEditMode={!bokforderContext.isEditMode}
                name="TEXT"
                size="large"
                isRequired
              />
            </EDBox>
          </EDColumn>
        )}

        {bokforderContext.isEditMode && (
          <EDColumn md={4} pt={0}>
            <EDBox sx={{ flex: 1 }}>
              <AttestantPicker
                isDisabled={false}
                kontodelDefFromKodplan={kontodelDefFromKodplan}
                setValdAttestant={setValdAttestant}
                type={TEDEKBokforderTYPEnum.UTANORDNING}
                valdAttestant={valdAttestant}
              />
            </EDBox>
          </EDColumn>
        )}
      </EDColumns>
      {validMessageUtbetalningsatt.includes(
        formContext?.formState.currentValues?._Associations?.Utbetalningssatt
          ?.BETSATT
      ) && (
        <EDColumns spacing={columnSpacing}>
          <EDColumn md={8} pt={0}>
            <EDBox sx={{ flex: 2 }}>
              <Attribut
                customDisplayLabel="Rubrik"
                disableEditMode={!bokforderContext.isEditMode}
                name="TEXT"
                size="large"
                isRequired
              />
            </EDBox>
          </EDColumn>
        </EDColumns>
      )}
    </EDBox>
  );
};

export default UtanordningBankontoFormView;

export const UtanordningBankkontoCard: IObjektVy = {
  type: 'ObjektVy',
  id: 'UtanordningBankkontoForm',
  info: {},
  def: () => UtanordningBankkontoFormComp,
};
